import React, { useEffect, useMemo, useState } from 'react';
import { Minus, Pause, Play, Plus, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import {
  METRONOME_AUDIO_SRC,
  METRONOME_BPM_OPTIONS,
  getAdjacentMetronomeBpm,
  getMetronomeIntervalMs,
  normalizeMetronomeBpm,
} from './modes/speedReadingShared';

type MetronomeState = {
  bpm: number;
  running: boolean;
  muted: boolean;
  beat: number;
  lastBeatAt: number | null;
};

const STORAGE_KEY = 'speed-reading-metronome-settings';
const THEME_PRIMARY = 'var(--theme-primary, #0ea5b7)';
const THEME_PRIMARY_DARK = 'var(--theme-primary-dark, #0f4c5c)';
const THEME_PRIMARY_SOFT = 'color-mix(in srgb, var(--theme-primary, #0ea5b7) 10%, white)';
const THEME_ACCENT = 'var(--theme-secondary, var(--theme-primary, #0ea5b7))';

const clampBpm = (value: number) => normalizeMetronomeBpm(value, 100);

const readStoredSettings = () => {
  if (typeof window === 'undefined') return { bpm: 100, muted: false };

  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      bpm: clampBpm(Number(parsed.bpm) || 100),
      muted: Boolean(parsed.muted),
    };
  } catch {
    return { bpm: 100, muted: false };
  }
};

const storedSettings = readStoredSettings();

let metronomeState: MetronomeState = {
  bpm: storedSettings.bpm,
  running: false,
  muted: storedSettings.muted,
  beat: 0,
  lastBeatAt: null,
};

let tickAudio: HTMLAudioElement | null = null;
let timerId: number | null = null;
const listeners = new Set<(state: MetronomeState) => void>();

const emitMetronomeState = () => {
  const snapshot = { ...metronomeState };
  listeners.forEach((listener) => listener(snapshot));
};

const persistMetronomeSettings = () => {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
    bpm: metronomeState.bpm,
    muted: metronomeState.muted,
  }));
};

const ensureTickAudio = () => {
  if (typeof window === 'undefined') return null;

  if (!tickAudio) {
    tickAudio = new Audio(METRONOME_AUDIO_SRC);
    tickAudio.preload = 'auto';
  }

  return tickAudio;
};

const playTickSound = () => {
  if (metronomeState.muted) return;

  const audio = ensureTickAudio();
  if (!audio) return;

  audio.currentTime = 0;
  audio.play().catch(() => { });
};

const updateMetronomeState = (updater: (current: MetronomeState) => MetronomeState) => {
  metronomeState = updater(metronomeState);
  emitMetronomeState();
};

const tickMetronome = () => {
  updateMetronomeState((current) => ({
    ...current,
    beat: current.beat + 1,
    lastBeatAt: Date.now(),
  }));
  playTickSound();
};

const stopTimer = () => {
  if (timerId !== null) {
    window.clearInterval(timerId);
    timerId = null;
  }
};

const startTimer = () => {
  if (typeof window === 'undefined') return;

  stopTimer();
  const interval = getMetronomeIntervalMs(metronomeState.bpm);
  timerId = window.setInterval(tickMetronome, interval);
};

const setMetronomeRunning = (running: boolean) => {
  if (running) {
    ensureTickAudio();
    updateMetronomeState((current) => ({ ...current, running: true }));
    tickMetronome();
    startTimer();
  } else {
    stopTimer();
    updateMetronomeState((current) => ({ ...current, running: false }));
  }
};

const setMetronomeBpm = (bpm: number) => {
  updateMetronomeState((current) => ({ ...current, bpm: clampBpm(bpm) }));
  persistMetronomeSettings();
  if (metronomeState.running) startTimer();
};

const setMetronomeMuted = (muted: boolean) => {
  updateMetronomeState((current) => ({ ...current, muted }));
  persistMetronomeSettings();
};

const resetMetronome = () => {
  stopTimer();
  updateMetronomeState((current) => ({
    ...current,
    bpm: 100,
    running: false,
    beat: 0,
    lastBeatAt: null,
  }));
  persistMetronomeSettings();
};

const subscribeMetronome = (listener: (state: MetronomeState) => void) => {
  listeners.add(listener);
  listener({ ...metronomeState });

  return () => {
    listeners.delete(listener);
  };
};

const getMetronomeState = () => ({ ...metronomeState });

export const configureSpeedReadingMetronome = (bpm: number) => {
  setMetronomeBpm(bpm);
};

export const pauseSpeedReadingMetronome = () => {
  if (metronomeState.running) {
    stopTimer();
    updateMetronomeState((current) => ({ ...current, running: false }));
  }
};

const formatElapsed = (lastBeatAt: number | null, beat: number, bpm: number) => {
  if (!lastBeatAt || beat <= 0) return '00:00';

  const elapsedSeconds = Math.max(0, Math.round((beat - 1) * (60 / clampBpm(bpm))));
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const SpeedReadingMetronomePanel: React.FC = () => {
  const [state, setState] = useState<MetronomeState>(() => getMetronomeState());
  const bpmIndex = useMemo(() => METRONOME_BPM_OPTIONS.findIndex((bpm) => bpm === normalizeMetronomeBpm(state.bpm, 100)), [state.bpm]);
  const progress = useMemo(() => (bpmIndex / (METRONOME_BPM_OPTIONS.length - 1)) * 100, [bpmIndex]);
  const elapsedLabel = useMemo(() => formatElapsed(state.lastBeatAt, state.beat, state.bpm), [state.beat, state.bpm, state.lastBeatAt]);

  useEffect(() => subscribeMetronome(setState), []);

  const updateRunning = (running: boolean) => {
    setMetronomeRunning(running);
    setState(getMetronomeState());
  };

  const updateBpm = (bpm: number) => {
    setMetronomeBpm(bpm);
    setState(getMetronomeState());
  };

  const updateMuted = (muted: boolean) => {
    setMetronomeMuted(muted);
    setState(getMetronomeState());
  };

  const updateReset = () => {
    resetMetronome();
    setState(getMetronomeState());
  };

  return (
    <div className="h-full bg-white text-slate-700">
      <div className="relative mx-auto flex h-full max-w-[1180px] flex-col px-8 py-10">
        <div className="relative z-20 grid min-h-10 grid-cols-[1fr_auto_1fr] items-center">
          <div />
          <div className="text-center text-[30px] font-semibold text-slate-400">Metronom</div>
          <div className="flex flex-wrap items-center justify-end gap-2 text-sm font-semibold text-slate-600">
            <button
              type="button"
              onClick={() => updateMuted(!state.muted)}
              className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 transition-colors hover:bg-slate-50"
              title={state.muted ? 'Sesi aç' : 'Sesi kapat'}
            >
              {state.muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              <span className="hidden sm:inline">{state.muted ? 'Sessiz' : 'Ses Açık'}</span>
            </button>
            <button
              type="button"
              onClick={updateReset}
              className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 transition-colors hover:bg-slate-50"
              title="Sıfırla"
            >
              <RotateCcw className="h-4 w-4" />
              <span className="hidden sm:inline">Sıfırla</span>
            </button>
            <div className="h-10 rounded-lg border border-slate-200 bg-white px-3 leading-[38px]">
              Süre {elapsedLabel}
            </div>
          </div>
        </div>

        <div className="relative z-20 mt-[100px] flex justify-center">
          <button
            type="button"
            onClick={() => updateRunning(!state.running)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white shadow-[0_10px_24px_rgba(15,76,92,0.22)] transition-transform hover:scale-105"
            style={{ backgroundColor: THEME_ACCENT }}
            title={state.running ? 'Durdur' : 'Başlat'}
          >
            {state.running ? <Pause className="h-4 w-4 fill-current" /> : <Play className="ml-0.5 h-4 w-4 fill-current" />}
          </button>
        </div>

        <div className="pointer-events-none relative z-10 -mt-12 flex items-center gap-6">
          <div className="min-w-[110px] text-slate-400">
            <span className="text-[26px] font-semibold leading-none">{state.bpm}</span>
            <span className="ml-1 text-xs font-semibold">BPM</span>
          </div>
        </div>

        <div className="relative z-10 mt-8 grid grid-cols-[22px_1fr_22px] items-center gap-4">
          <button
            type="button"
            onClick={() => updateBpm(getAdjacentMetronomeBpm(state.bpm, -1))}
            className="transition-transform hover:scale-110"
            style={{ color: THEME_PRIMARY }}
            title="BPM azalt"
          >
            <Minus className="h-5 w-5" />
          </button>
          <input
            type="range"
            min={0}
            max={METRONOME_BPM_OPTIONS.length - 1}
            step={1}
            value={bpmIndex}
            onChange={(event) => updateBpm(METRONOME_BPM_OPTIONS[Number(event.target.value)] || 100)}
            onInput={(event) => updateBpm(METRONOME_BPM_OPTIONS[Number((event.target as HTMLInputElement).value)] || 100)}
            className="speed-reading-metronome-slider w-full"
            style={{
              '--metronome-progress': `${progress}%`,
              '--metronome-primary': THEME_PRIMARY,
              '--metronome-primary-dark': THEME_PRIMARY_DARK,
            } as React.CSSProperties}
            aria-label="Metronom hızı"
          />
          <button
            type="button"
            onClick={() => updateBpm(getAdjacentMetronomeBpm(state.bpm, 1))}
            className="transition-transform hover:scale-110"
            style={{ color: THEME_PRIMARY }}
            title="BPM artır"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>

        <div className="relative z-10 mt-10 flex flex-wrap items-center justify-center gap-3">
          {METRONOME_BPM_OPTIONS.map((bpm) => (
            <button
              key={bpm}
              type="button"
              onClick={() => updateBpm(bpm)}
              className="h-10 rounded-lg border px-4 text-sm font-bold transition-colors hover:bg-slate-50"
              style={{
                borderColor: state.bpm === bpm ? THEME_PRIMARY : '#e2e8f0',
                backgroundColor: state.bpm === bpm ? THEME_PRIMARY_SOFT : '#fff',
                color: state.bpm === bpm ? THEME_PRIMARY_DARK : '#64748b',
              }}
            >
              {bpm}
            </button>
          ))}
        </div>
      </div>

      <style>{`
        .speed-reading-metronome-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          border-radius: 999px;
          background: linear-gradient(90deg, var(--metronome-primary-dark) 0%, var(--metronome-primary-dark) var(--metronome-progress), #edf0f1 var(--metronome-progress), #edf0f1 100%);
          outline: none;
        }

        .speed-reading-metronome-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          height: 16px;
          width: 16px;
          border-radius: 999px;
          background: var(--metronome-primary-dark);
          border: 0;
          cursor: pointer;
        }

        .speed-reading-metronome-slider::-moz-range-thumb {
          height: 16px;
          width: 16px;
          border-radius: 999px;
          background: var(--metronome-primary-dark);
          border: 0;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default SpeedReadingMetronomePanel;
